import {
  Toast
} from 'vant'
import {handleThirdOrder} from "@/api/home";

// 串口通信管理类
class SerialPortManager {
  constructor() {
    this.port = null;
    this.reader = null;
    this.writer = null;
    this.isConnected = false;
    this.isListening = false;
    this.portInfo = '';
    this.packetSequence = 0;
    this.lastCommand = null;
    this.showBrowserWarning = false;
    this.listeners = new Set();
    this.lastSelectedPort = null;
    this.connecting = null;
    this.storageKey = 'serial-port-info';

    this.dataBuffer = []; // 通用缓冲区（存储 Hex 数组）
    this.currentTargetLength = null; // 动态目标长度（每个数据包可能不同）
    // 交易信息
    this.amount = 0;

    // 调试信息
    this.debugMessages = [];

    // 回调函数
    this.onDataReceived = null;
    this.onStatusChange = null;
    this.onDebugMessage = null;
  }
  // 添加数据监听器
  addDataListener(callback) {
    this.listeners.add(callback);
  }

  // 移除数据监听器
  removeDataListener(callback) {
    this.listeners.delete(callback);
  }
  // 检查浏览器支持情况
  checkBrowserSupport() {
    if (!navigator.serial) {
      this.showBrowserWarning = true;
      this._logDebug('error', '浏览器不支持 Web Serial API');

      if (this.onStatusChange) {
        this.onStatusChange('browser_unsupported');
      }

      return false;
    }
    return true;
  }

  // 连接设备
  async connect() {
    if (!this.checkBrowserSupport()) {
      throw new Error('浏览器不支持 Web Serial API');
    }
    if (this.isConnected) {
      if (!this.reader) this._startListening();
      return true;
    }
    if (this.connecting) return this.connecting;

    this.connecting = (async () => {
      try {
        let authorizedPorts = [];
        if (typeof navigator.serial.getPorts === 'function') {
          try {
            authorizedPorts = await navigator.serial.getPorts();
          } catch (error) {
            this._logDebug('warn', `获取已授权串口失败: ${error.message}`);
          }
        }

        let savedInfo = null;
        try {
          savedInfo = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
        } catch (error) {
          localStorage.removeItem(this.storageKey);
        }

        const savedPort = authorizedPorts.find(port => {
          if (!savedInfo) return false;
          const info = port.getInfo ? port.getInfo() : {};
          return info.usbVendorId === savedInfo.usbVendorId &&
            info.usbProductId === savedInfo.usbProductId;
        });

        this.port = this.lastSelectedPort || savedPort || authorizedPorts[0] || null;
        if (!this.port) this.port = await navigator.serial.requestPort();
        this.lastSelectedPort = this.port;

        await this.port.open({
          baudRate: 9600,
          dataBits: 8,
          stopBits: 1,
          parity: 'none'
        });

        const selectedInfo = this.port.getInfo ? this.port.getInfo() : {};
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(selectedInfo));
        } catch (error) {}

        this.isConnected = true;
        this.portInfo = this.port.path ? `路径: ${this.port.path}` : 'USB 转串口设备';
        this._logDebug('success', `-----------------------已连接到设备: ${this.portInfo}`);
        if (this.onStatusChange) this.onStatusChange('connected', this.portInfo);
        this._startListening();
        return true;
      } catch (error) {
        this.port = null;
        this.isConnected = false;
        this._logDebug('fail', `连接失败: ${error.message}`);
        if (error.message.includes('permissions policy')) this.showBrowserWarning = true;
        if (this.onStatusChange) this.onStatusChange('fail', error.message);
        throw new Error('连接设备失败: ' + error.message);
      }
    })();

    try {
      return await this.connecting;
    } finally {
      this.connecting = null;
    }
  }

  // 断开连接
  async disconnect() {
    this.isListening = false;
    if (this.reader) {
      await this.reader.cancel();
      this.reader = null;
    }
    if (this.writer) {
      await this.writer.releaseLock();
      this.writer = null;
    }
    if (this.port) {
      await this.port.close();
      this.port = null;
    }
    this.isConnected = false;
    this.portInfo = '';
    this._logDebug('fail', '-----------------------已断开设备连接');

    if (this.onStatusChange) {
      this.onStatusChange('disconnected');
    }
  }

  // 开始监听串口数据
  async _startListening() {
    // 防止重复启动，先开启监听标记
    if (this.isListening) return;
    this.isListening = true;

    while (this.isListening && this.port && this.port.readable) {
      this.reader = this.port.readable.getReader();
      try {
        while (true) {
          const {
            value,
            done
          } = await this.reader.read();
          if (done) {
            // this._logDebug('success', '读取已完成');
            break;
          }
          if (value) {
            this._processReceivedData(value);
          }
        }
      } catch (error) {
        this._logDebug('fail', `读取数据错误: ${error.message}`);
        break;
      } finally {
        if (this.reader) {
          await this.reader.cancel();
          await this.reader.releaseLock();
          this.reader = null;
        }
      }
      // 循环结束后重置监听标记
      this.isListening = false;
    }
  }

  // 发送命令
  async sendCommand(hexString) {
    if (!this.isConnected) {
      return;
    }

    // 发送数据
    this.writer = this.port.writable.getWriter();
    try {
      const data = this._hexToUint8Array(hexString);
      if (!data) {
        throw new Error('无效的 Hex 字符串');
      }

      await this.writer.write(data);
      this._logDebug('', `发送命令: ${hexString}`);
      this.lastCommand = hexString;

    } catch (error) {
      this._logDebug('fail', `发送命令失败: ${error.message}`);
      throw error;
    } finally {
      await this.writer.releaseLock();
      this.writer = null;
    }
  }

  // 取消交易
  async cancelTransaction() {
    // 取消支付的固定指令
    const cancelCommand = 'AA0100AB';
    await this.sendCommand(cancelCommand);
  }

  // 处理接收到的数据
  _processReceivedData(data) {
    const newHexData = this._bytesToHex(data);
    this.dataBuffer.push(...newHexData);
    // this._logDebug('info', `新数据加入缓冲区: [${newHexData.join(' ')}] (长度: ${newHexData.length})`);
    // 处理缓冲区
    this._processBuffer();
  }

  // 解析缓冲区，动态匹配目标长度
  _processBuffer() {
    // 循环处理，避免缓冲区残留多个完整数据包
    while (true) {
      // 1. 查找以 "AA" 开始的位置
      const aaIndex = this.dataBuffer.indexOf('AA');
      // 缓冲区没有以 AA 开始的数据时，清空无效数据
      if (aaIndex === -1) {
        this.dataBuffer = [];
        this.currentTargetLength = null;
        return;
      }
      // 清除 AA 前的无效数据
      if (aaIndex > 0) {
        const invalidData = this.dataBuffer.slice(0, aaIndex);
        this._logDebug('warn', `移除 AA 前无效数据（长度: ${aaIndex}）: [${invalidData.join(' ')}]`);
        this.dataBuffer.splice(0, aaIndex);
        continue;
      }
      // 2. 缓冲区已由 AA 开始，检查长度字段是否完整
      if (this.dataBuffer.length < 3) {return;}

      // 3. 读取长度字段并设置目标数据包长度
      const lengthFlag = this.dataBuffer[1]; // E0 或 80
      this.currentTargetLength = parseInt(this.dataBuffer[2], 16)+4;
      // 4. 检查缓冲区是否达到目标长度
      if (this.dataBuffer.length < this.currentTargetLength) {return;}

      // 5. 长度足够时提取并处理完整数据包
      const completePacket = this.dataBuffer.splice(0, this.currentTargetLength);
      this._logDebug('success', `提取完整数据包（AA+${lengthFlag}）: [${completePacket.join(' ')}] (长度: ${completePacket.length})`);
      if (completePacket[1] === '80') {
        this.listeners.forEach(callback => {
          callback(completePacket);
        });
      }
      // 6. 缓冲区仍有剩余数据时继续处理，避免粘包
      if (this.dataBuffer.length === 0) break;
    }
  }

  // Hex 字符串转 Uint8Array
  _hexToUint8Array(hexString) {
    // 移除所有空格
    hexString = hexString.replace(/\s/g, '');
    // 检查长度是否为偶数
    if (hexString.length % 2 !== 0) {
      return null;
    }
    const array = new Uint8Array(hexString.length / 2);
    for (let i = 0; i < hexString.length; i += 2) {
      const byte = parseInt(hexString.substr(i, 2), 16);
      if (isNaN(byte)) {
        return null;
      }
      array[i / 2] = byte;
    }
    return array;
  }

  // Uint8Array 转 Hex 字符串数组
  _bytesToHex(uint8Array) {
    return Array.from(uint8Array)
      .map(byte => byte.toString(16).padStart(2, '0').toUpperCase())
  }
  // 记录调试信息
  _logDebug(type, message) {
    const time = new Date().toLocaleTimeString();
    const debugEntry = {
      time,
      type,
      data: message
    };

    // Toast({
    //   type,
    //   message
    // });

    console.log(message);


    this.debugMessages.push(debugEntry);

    // 限制调试信息数量
    if (this.debugMessages.length > 100) {
      this.debugMessages.shift();
    }

    if (this.onDebugMessage) {
      this.onDebugMessage(debugEntry);
    }
  }


  /**
   * 判断是否为 POS 机返回数据
   * @param {string} hex - 十六进制字符串
   * @returns {boolean}
   */
  isPayPacket(hex) {
    const BAO_TOU = 'AA';
    const CODE_80 = '80';

    // 检查字符串是否为空或长度不足
    if (!hex || hex.length < 8) {
      return false;
    }

    // 提取前四个字符
    const prefix = hex.substring(0, 4);
    // 检查前两位是否为 AA，后两位是否为 80
    if (!(prefix.startsWith(BAO_TOU) && prefix.endsWith(CODE_80))) {
      return false;
    }

    // 校验位判断
    return this.stepSumHexBytes(hex.substring(0, hex.length - 2)) === hex.substring(hex.length - 2);
  }

  /**
   * 计算十六进制字节累加和（保留低 8 位）
   * @param {string} hexString - 十六进制字符串
   * @returns {string|null} 两位十六进制校验码
   */
  stepSumHexBytes(hexString) {
    // 验证输入
    if (!hexString || hexString.length % 2 !== 0) {
      return null;
    }

    let sum = 0;
    // 每两位作为一个十六进制字节进行遍历
    for (let i = 0; i < hexString.length; i += 2) {
      // 截取两位十六进制数
      const hexByte = hexString.substring(i, i + 2);
      try {
        // 转换为十进制
        const byteValue = parseInt(hexByte, 16);

        // 累加并截断超过低 8 位的部分
        sum += byteValue;
        sum = sum & 0xFF; // 保留低 8 位
      } catch (e) {
        // 无效的十六进制字符
        return null;
      }
    }

    // 转换为两位大写十六进制字符串
    return sum.toString(16).toUpperCase().padStart(2, '0');
  }

}

// 导出单例实例
const serialPortManager = new SerialPortManager();
export default serialPortManager;

